import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Phone, ShieldCheck, Clock, MapPin, Utensils, Coffee, Cookie, ArrowRight, FileDown, Camera, DoorOpen, Lock, Crown, Pencil, X, Mail, User, Save, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Panel, Eyebrow, MonoLabel, LiveDot } from "@/components/Panel";
import { OtpInput } from "@/components/OtpInput";
import { RealQR } from "@/components/RealQR";
import { MemberGalleryPanel } from "@/pages/MemberGallery";
import { useDemo } from "@/store/DemoStore";
import { MEMBERS, MEAL_WINDOWS, ROUNDS, SEATING, findMember, CATEGORIES, CHAPTERS } from "@/data/seed";
import { Toaster, toast } from "sonner";
import { otpApi, apiErr, MEMBER_TOKEN_KEY } from "@/lib/authApi";

const SHOW_AUTOFILL = process.env.REACT_APP_DEMO_OTP_AUTOFILL === "true";

export default function MemberPortal() {
  const { state, dispatch } = useDemo();
  const [step, setStep] = useState(state.otpVerified ? "dashboard" : "phone"); // phone | otp | dashboard
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["","","","","",""]);
  const [otpReq, setOtpReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [booting, setBooting] = useState(!!localStorage.getItem(MEMBER_TOKEN_KEY));
  const inputs = React.useRef([]);

  const hydrate = React.useCallback(async () => {
    const me = await otpApi.me();
    const r = me.registration;
    dispatch({ type: "REGISTER_MEMBER", member: {
      id: r.id.slice(0, 8).toUpperCase(), regId: r.id, name: r.name, phone: r.phone, email: r.email, chapter: r.chapter || "—", region: r.city || "Davangere",
      category: r.business_category || "—", tier: r.tier_id, tierName: me.tier?.name, fee: (me.effective_total_paise ?? (me.payment?.amount_paise ?? 0)) / 100,
      photo: r.photo_url ? (r.photo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${r.photo_url}?v=${encodeURIComponent(r.photo_at || "")}` : r.photo_url) : null,
      mode: r.registration_type, stallName: r.registration_type === "stall" ? r.name : undefined,
      contactPerson: r.custom_fields?.contact_person, whatsapp: me.whatsapp, event: me.event,
      mealWindows: me.meal_windows || [], checkins: me.checkins || [],
      leaderAssignments: me.leader_assignments || [],
    } });
    dispatch({ type: "SET_OTP_VERIFIED", value: true });
    setStep("dashboard");
    return r;
  }, [dispatch]);

  // Already signed in (e.g. via the unified login modal)? Skip the phone/OTP gate.
  useEffect(() => {
    if (!localStorage.getItem(MEMBER_TOKEN_KEY)) return;
    hydrate()
      .catch(() => { localStorage.removeItem(MEMBER_TOKEN_KEY); dispatch({ type: "SET_OTP_VERIFIED", value: false }); setStep("phone"); })
      .finally(() => setBooting(false));
    // eslint-disable-next-line
  }, []);

  const sendOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await otpApi.request(phone);
      setOtpReq(r); setStep("otp"); setOtp(["","","","","",""]);
      toast.success(`OTP sent on WhatsApp to ${r.sent_to}`);
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };

  // Use a registered member if it exists (from registration flow), otherwise pick a demo one.
  // For stall registrations, derive display fields from stallName / contactPerson.
  const member = React.useMemo(() => {
    if (!state.registeredMember) return MEMBERS[0];
    const r = state.registeredMember;
    const isStall = r.mode === "stall";
    return {
      ...MEMBERS[0],
      ...r,
      avatar: r.photo || MEMBERS[0].avatar,
      // Present stall registrations as the business, with the contact person shown below.
      name: isStall ? (r.stallName || r.name) : r.name,
      subtitle: isStall ? (r.contactPerson ? `Contact · ${r.contactPerson}` : null) : null,
      isStall,
    };
  }, [state.registeredMember]);

  useEffect(() => {
    if (state.otpVerified) setStep("dashboard");
  }, [state.otpVerified]);

  const submitOtp = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : otp.join("")).replace(/\D/g, "");
    if (code.length !== 6) return toast.error("Enter all 6 digits");
    setErr(""); setBusy(true);
    try {
      const v = await otpApi.verify(phone, code, otpReq?.request_id);
      localStorage.setItem(MEMBER_TOKEN_KEY, v.token);
      const r = await hydrate();
      toast.success(`Verified · Welcome ${r.name.split(" ")[0]}`);
    } catch (e) { setErr(apiErr(e)); setOtp(["", "", "", "", "", ""]); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 }
      }} />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <Eyebrow>Member Portal</Eyebrow>
        <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Your Members Day, in your pocket.</h1>

        {booting ? (
          <div className="mt-10 mono-label" data-testid="member-portal-booting">Signing you in…</div>
        ) : step !== "dashboard" ? (
          <div className="mt-10 max-w-[440px]">
            <div className="flex justify-center mb-6" data-testid="member-login-logo-wrap">
              <img src="/risse-bni-dark.webp" alt="RISSE BNI 26 · Davanagere Members Day" className="h-28 w-auto" data-testid="member-login-logo" />
            </div>
            <Panel className="panel-glass p-6">
              {step === "phone" && (
                <>
                  <MonoLabel>Sign in with your WhatsApp number</MonoLabel>
                  <label className="mt-4 block">
                    <div className="mono-label mb-2">Phone Number</div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} style={{ color: "var(--taupe)" }} />
                      <input data-testid="member-phone-input" className="input-x" placeholder="+91 98765 43210" value={phone} onChange={(e)=>setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendOtp()} />
                    </div>
                  </label>
                  {err && <div data-testid="member-otp-error" className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div>}
                  <button data-testid="member-send-otp-btn" className="btn-red mt-5 w-full" disabled={busy} onClick={sendOtp}>
                    {busy ? "Sending…" : "Send OTP"}
                  </button>
                  <div className="mono-label mt-4" style={{ color: "var(--muted)" }}>Use the WhatsApp number you registered with. Code expires in 5 minutes.</div>
                </>
              )}
              {step === "otp" && (
                <>
                  <MonoLabel>Enter the 6-digit code</MonoLabel>
                  <div className="mt-4">
                    <OtpInput value={otp} onChange={setOtp} onComplete={(code) => submitOtp(code)} testIdPrefix="otp-input" disabled={busy} />
                  </div>
                  {err && <div data-testid="member-otp-error" className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div>}
                  <button data-testid="member-verify-btn" className="btn-red w-full mt-5" disabled={busy} onClick={() => submitOtp()}>
                    <ShieldCheck size={14} /> {busy ? "Verifying…" : "Verify"}
                  </button>
                  <div className="mt-3 flex items-center justify-between mono-label" style={{ color: "var(--taupe)" }}>
                    <span>Sent to {otpReq?.sent_to}</span>
                    <button data-testid="member-resend-otp" className="hover:text-[var(--red)]" disabled={busy} onClick={sendOtp}>Resend</button>
                  </div>
                  {SHOW_AUTOFILL && (
                    <button data-testid="member-autofill-btn" className="btn-ghost mt-3 w-full" onClick={() => setOtp(["1","2","3","4","5","6"])}>
                      Autofill demo code
                    </button>
                  )}
                </>
              )}
            </Panel>
          </div>
        ) : (
          <MemberDashboard member={member} onPhotoUpdated={hydrate} />
        )}
      </div>
    </div>
  );
}

function MemberDashboard({ member, onPhotoUpdated }) {
  const nav = useNavigate();
  const [flipped, setFlipped] = useState(false);
  const [now, setNow] = useState(new Date());
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  // Personal recap unlocks AFTER the event ends (Asia/Kolkata end-of-day on event.date).
  const recapUnlocked = React.useMemo(() => {
    const d = member?.event?.date;
    if (!d) return false;
    const eventEnd = new Date(`${d}T23:59:59+05:30`);
    return Date.now() > eventEnd.getTime();
  }, [member?.event?.date]);
  const eventDatePretty = React.useMemo(() => {
    const d = member?.event?.date;
    if (!d) return "the event";
    try { return new Date(d + "T00:00:00+05:30").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return d; }
  }, [member?.event?.date]);

  const pickPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Photo is too large — please pick one under 8 MB");
    setUploading(true);
    try {
      await otpApi.uploadPhoto(file);
      await onPhotoUpdated?.();
      toast.success("Profile photo updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  // Show initials whenever the member has NOT uploaded a real profile photo
  // (don't fall back to the demo stock image).
  const initials = React.useMemo(() => {
    const source = member.isStall ? (member.stallName || member.name || "") : (member.name || "");
    return source.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");
  }, [member]);
  const useInitials = !member.photo;

  // Find seat assignments across rounds for this member.
  // For freshly registered members/stalls that aren't in the seed SEATING map,
  // derive a stable table number from the id so it doesn't always show Table #1.
  const stableTable = React.useMemo(() => {
    const s = String(member.id);
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return (h % 50) + 1;
  }, [member.id]);
  const assignments = ROUNDS.map((r) => {
    const table = SEATING[r.id].find((t) => t.seats.some(s => s.memberId === member.id));
    return { round: r, table: table?.tableNo || stableTable };
  });

  // Real check-in state: a row only turns green once a scanner granted it
  const ICON_FOR = (name, kind) => {
    if (kind === "entry") return DoorOpen;
    const n = (name || "").toLowerCase();
    if (n.includes("lunch")) return Utensils;
    if (n.includes("tea") || n.includes("snack")) return Cookie;
    return Coffee;
  };
  const scannedAt = React.useMemo(() => {
    const m = {};
    (member.checkins || []).forEach((c) => { m[c.meal_window_id] = c.scanned_at; });
    return m;
  }, [member.checkins]);
  const checkinRows = (member.mealWindows || []).map((w) => ({
    key: w.id, name: w.name, kind: w.kind || "meal", icon: ICON_FOR(w.name, w.kind),
    at: scannedAt[w.id] || null,
  }));
  const entryDone = checkinRows.some((r) => r.kind === "entry" && r.at);
  const hasEntry = checkinRows.some((r) => r.kind === "entry");

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Left: profile + logout */}
      <Panel className="panel-glass p-6 lg:col-span-4">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            {useInitials ? (
              <div className="w-16 h-16 border border-[var(--hairline)] bg-[var(--panel-2)] flex items-center justify-center font-serif text-xl" style={{ color: "var(--red-2)" }}>
                {initials || "MD"}
              </div>
            ) : (
              <img src={member.avatar} alt="" className="w-16 h-16 object-cover border border-[var(--hairline)]" />
            )}
            <div className="mt-2 flex items-center gap-2">
              <label className="mono-label cursor-pointer hover:text-[var(--red)]" style={{ color: "var(--red-2)" }}>
                {uploading ? "SAVING…" : "CHANGE"}
                <input data-testid="member-photo-upload" type="file" accept="image/*" className="hidden" disabled={uploading}
                  onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              <label className="mono-label cursor-pointer hover:text-[var(--red)]" style={{ color: "var(--taupe)" }} title="Take a photo">
                <Camera size={13} className="inline" />
                <input data-testid="member-photo-camera" type="file" accept="image/*" capture="user" className="hidden" disabled={uploading}
                  onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          <div className="min-w-0">
            <div className="mono-label">{member.tierName || member.tier}</div>
            <div className="font-serif text-2xl mt-1 truncate">{member.name}</div>
            {member.subtitle ? (
              <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{member.subtitle}</div>
            ) : (
              <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{member.chapter} · {member.region}</div>
            )}
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4">
          <Kv k={member.isStall ? "Stall ID" : "Member ID"} v={member.id} />
          <Kv k="Category" v={member.category} />
          <Kv k="Phone" v={member.phone} />
          <Kv k="Email" v={member.email || "—"} />
          <Kv k="Fee paid" v={member.fee ? `₹${member.fee.toLocaleString("en-IN")}` : "—"} />
          {member.isStall && member.contactPerson && (
            <Kv k="Contact person" v={member.contactPerson} />
          )}
        </div>

        <button
          type="button"
          data-testid="member-edit-profile-btn"
          onClick={() => setEditOpen(true)}
          className="btn-ghost mt-5 w-full"
        >
          <Pencil size={13} /> Edit personal details
        </button>
        <TooltipProvider delayDuration={120}>
          <Tooltip>
            <TooltipTrigger asChild>
              {recapUnlocked ? (
                <a
                  data-testid="member-recap-btn"
                  href={`/recap/${member.id}?print=1`}
                  target="_blank" rel="noreferrer"
                  className="btn-red mt-6 w-full"
                >
                  <FileDown size={14} /> Download personal recap
                </a>
              ) : (
                <button
                  type="button"
                  data-testid="member-recap-btn-locked"
                  aria-disabled="true"
                  onClick={(e) => { e.preventDefault(); toast(`This will be enabled after the event. Please check this after ${eventDatePretty}.`); }}
                  className="btn-ghost mt-6 w-full cursor-not-allowed opacity-60"
                  style={{ pointerEvents: "auto" }}
                >
                  <Lock size={14} /> Download personal recap
                </button>
              )}
            </TooltipTrigger>
            {!recapUnlocked && (
              <TooltipContent
                side="top"
                className="!bg-[var(--panel)] !border-[var(--hairline)] !text-[var(--text)] !rounded-none max-w-[260px]"
                data-testid="recap-locked-tooltip"
              >
                <div className="text-xs leading-relaxed">
                  This will be enabled after the event. Please check this after {eventDatePretty}.
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </Panel>

      {/* Center: digital pass */}
      <Panel className="panel-glass p-6 lg:col-span-4 flex flex-col items-center">
        <div className="w-full flex items-center justify-between">
          <MonoLabel>Digital Event Pass</MonoLabel>
          <button data-testid="pass-flip-btn" className="mono-label" style={{color:"var(--red-2)"}} onClick={() => setFlipped(!flipped)}>FLIP</button>
        </div>
        <div className="relative mt-4 w-full max-w-[300px]" style={{ perspective: 1200 }}>
          <motion.div
            animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.6 }}
            style={{ transformStyle: "preserve-3d", position: "relative", width: "100%", aspectRatio: "1/1.15" }}
          >
            {/* front */}
            <div className="absolute inset-0 bg-[var(--panel-2)] hairline p-4 flex flex-col items-center" style={{ backfaceVisibility: "hidden" }}>
              <RealQR regId={member.regId || member.id} size={220} />
              <div className="mono-label mt-3">{member.id}</div>
            </div>
            {/* back */}
            <div className="absolute inset-0 bg-[var(--panel-2)] hairline p-5 flex flex-col justify-between" style={{ transform: "rotateY(180deg)", backfaceVisibility: "hidden" }}>
              <div>
                <div className="mono-label">{member.isStall ? "Business" : "Attendee"}</div>
                <div className="font-serif text-xl mt-1 truncate">{member.name}</div>
                <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
                  {member.isStall ? (member.contactPerson ? `Contact · ${member.contactPerson}` : member.category) : member.chapter}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Kv k="Tier" v={member.tierName || member.tier} />
                <Kv k="Cat" v={member.category} />
              </div>
              <div className="mono-label" style={{ color: "var(--muted)" }}>BNI Davangere · 28 Nov 2026</div>
            </div>
          </motion.div>
        </div>
        <div className="mt-4 flex items-center gap-2"><LiveDot label={now.toLocaleTimeString()} /></div>
      </Panel>

      {/* Right: schedule + meals */}
      <div className="lg:col-span-4 space-y-6">
        {member.leaderAssignments?.length > 0 && (
          <Panel className="panel-glass p-6" data-testid="leader-cta-card">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Crown size={14} style={{ color: "var(--red-2)" }} />
                <MonoLabel style={{ color: "var(--red-2)" }}>You're leading</MonoLabel>
              </div>
              <span className="chip">{member.leaderAssignments.length} TABLE{member.leaderAssignments.length > 1 ? "S" : ""}</span>
            </div>
            <div className="text-sm mt-3" style={{ color: "var(--body)" }}>
              You've been assigned as the table leader. Open the leader screen below — no PIN needed, your WhatsApp sign-in already proves it's you.
            </div>
            <div className="mt-4 space-y-2">
              {member.leaderAssignments.map((la) => (
                <LeaderOpenButton key={la.assignment_id} assignment={la} />
              ))}
            </div>
          </Panel>
        )}

        <Panel className="panel-glass p-6">
          <div className="flex items-center justify-between">
            <MonoLabel>Your Conclave Schedule</MonoLabel>
            <span className="chip">{assignments.length} ROUNDS</span>
          </div>
          <div className="mt-4">
            {assignments.map((a, i) => (
              <button
                key={a.round.id}
                data-testid={`schedule-round-${a.round.id}`}
                onClick={() => nav(`/leader?round=${a.round.id}&table=${a.table}&highlight=${encodeURIComponent(member.id)}&view=1`)}
                className={`w-full flex items-center justify-between py-3 text-left transition-colors hover:bg-[rgba(204,37,47,0.06)] px-1 -mx-1 ${i < assignments.length - 1 ? "hairline-b" : ""}`}
              >
                <div>
                  <div className="text-sm font-medium">{a.round.label} <span style={{color:"var(--taupe)"}}>· {a.round.time}</span></div>
                  <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}><MapPin size={10} className="inline mr-1" /> Hall A · Row {String.fromCharCode(65 + (a.table%5))}</div>
                </div>
                <div className="text-right flex items-center gap-3">
                  <div>
                    <div className="mono-label">Table</div>
                    <div className="font-serif text-2xl">#{a.table}</div>
                  </div>
                  <ArrowRight size={16} style={{ color: "var(--red-2)" }} />
                </div>
              </button>
            ))}
          </div>
        </Panel>

        <Panel className="panel-glass p-6">
          <div className="flex items-center justify-between">
            <MonoLabel>Event check-ins</MonoLabel>
            {hasEntry && (
              <span className={`chip ${entryDone ? "green" : ""}`} data-testid="member-entry-chip">
                {entryDone ? "CHECKED IN" : "NOT CHECKED IN"}
              </span>
            )}
          </div>
          {hasEntry && !entryDone && (
            <div className="mono-label mt-3" style={{ color: "var(--taupe)" }}>
              Show this QR at the reception desk when you arrive — meals open up after entry check-in.
            </div>
          )}
          <div className="mt-4 space-y-3">
            {checkinRows.length === 0 && <div className="mono-label" style={{ color: "var(--muted)" }}>No check-in windows configured yet</div>}
            {checkinRows.map((m) => (
              <div key={m.key} className="flex items-center justify-between hairline p-3" data-testid={`member-checkin-${m.kind === "entry" ? "entry" : m.key}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <m.icon size={16} />
                  <span className="text-sm truncate">{m.name}</span>
                </div>
                <span className={`chip ${m.at ? "green" : ""}`} title={m.at ? new Date(m.at).toLocaleString("en-IN") : ""}>
                  {m.at ? `CHECKED IN · ${new Date(m.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "PENDING"}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Full-width Photo Gallery panel */}
      <div className="lg:col-span-12">
        <MemberGalleryPanel />
      </div>

      <AnimatePresence>
        {editOpen && (
          <EditProfileModal
            member={member}
            onClose={() => setEditOpen(false)}
            onSaved={async () => { setEditOpen(false); await onPhotoUpdated?.(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function EditProfileModal({ member, onClose, onSaved }) {
  const [name, setName] = useState(member.isStall ? (member.stallName || member.name || "") : (member.name || ""));
  const [phone, setPhone] = useState(member.phone || "");
  const [email, setEmail] = useState(member.email || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (trimmedName.length < 2) return toast.error("Please enter a valid name");
    if (trimmedEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) return toast.error("Please enter a valid email");
    if (!/^\+?\d[\d\s\-()]{7,15}$/.test(trimmedPhone)) return toast.error("Please enter a valid phone number");
    setBusy(true);
    try {
      await otpApi.updateProfile({ name: trimmedName, phone: trimmedPhone, email: trimmedEmail || null });
      toast.success("Profile updated");
      await onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="member-edit-profile-modal"
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px]"
      >
        <Panel className="p-6 relative">
          <button data-testid="member-edit-close" onClick={onClose} className="absolute right-4 top-4 p-1 hover:text-[var(--red)]"><X size={16} /></button>
          <MonoLabel>Edit personal details</MonoLabel>
          <h2 className="font-serif text-2xl mt-2">Keep your info current</h2>
          <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
            Updates reflect on your pass, invoice resends, and WhatsApp notifications.
          </div>

          <div className="mt-5 space-y-4">
            <label className="block">
              <div className="mono-label mb-2 flex items-center gap-2"><User size={12} /> Full name</div>
              <input data-testid="member-edit-name" className="input-x" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="block">
              <div className="mono-label mb-2 flex items-center gap-2"><Phone size={12} /> Phone / WhatsApp</div>
              <input data-testid="member-edit-phone" className="input-x" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>
                Changing your phone will update where WhatsApp confirmations are sent.
              </div>
            </label>
            <label className="block">
              <div className="mono-label mb-2 flex items-center gap-2"><Mail size={12} /> Email</div>
              <input data-testid="member-edit-email" type="email" className="input-x" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
          </div>

          <div className="mt-6 flex gap-3">
            <button data-testid="member-edit-save" onClick={save} disabled={busy} className="btn-red">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {busy ? "Saving…" : "Save changes"}
            </button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </Panel>
      </motion.div>
    </motion.div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="mono-label">{k}</div>
      <div className="text-sm mt-1" style={{ overflowWrap: "anywhere" }}>{v}</div>
    </div>
  );
}


function LeaderOpenButton({ assignment }) {
  const nav = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem(MEMBER_TOKEN_KEY);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leader/member-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ round_number: assignment.round_number, table_number: assignment.table_number }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Could not open leader screen");
      localStorage.setItem("bni-leader", JSON.stringify({
        name: data.leader_name, phone: data.leader_phone, session_id: data.session_id,
      }));
      nav(`/leader?round=${assignment.round_number}&table=${assignment.table_number}`);
    } catch (e) {
      toast.error(String(e.message || "Could not open leader screen"));
    } finally { setBusy(false); }
  };
  const label = `Round ${assignment.round_number || "?"}`;
  return (
    <button
      type="button"
      data-testid={`leader-open-${assignment.round_number}-${assignment.table_number}`}
      disabled={busy}
      onClick={open}
      className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-[var(--hairline)] hover:border-[rgba(226,62,71,0.5)] transition-colors text-left"
      style={{ background: "linear-gradient(180deg, rgba(226,62,71,0.08), rgba(226,62,71,0.02))" }}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>Table #{assignment.table_number} · {busy ? "Opening…" : "Tap to lead"}</div>
      </div>
      <ArrowRight size={16} style={{ color: "var(--red-2)" }} />
    </button>
  );
}
